// Shared by Play and the server. IDs and geometry remain usable while names
// and authored descriptions are presented according to character knowledge.
const idOf = value => String(value?.idEntity || value?.placeId || value?.targetPlaceId || value?.targetId || '').trim();
const labelFor = place => {
  if (place?.publicLabel) return String(place.publicLabel);
  const type = String(place?.buildingType || place?.transitPointType || place?.placeType || 'place');
  const noun = /^\d+$/.test(type) ? 'place' : type.toLowerCase();
  return `${/^[aeiou]/.test(noun) ? 'an' : 'a'} ${noun}`;
};
const escapePattern = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const visibleSignText = signs => (signs || []).flatMap(sign => [sign.readable ? sign.text : '', /^an?\s/i.test(sign.symbol || '') ? sign.symbol : '']).filter(Boolean);
const createPlaceIdentity = (places = [], knowledge = {}, visibleTexts = []) => {
  const byId = new Map(places.map(place => [idOf(place), place]).filter(([id]) => id));
  // Common geographical names can also be namesakes of unidentified buildings.
  // Keep those words usable in prose; specific place records still use their
  // own knowledge entry, and longer private names remain subject to filtering.
  const publicGeography = new Set([...byId.values()].filter(place =>
    knowledge[idOf(place)]?.learnedName && knowledge[idOf(place)].locationKnown)
    .map(place => knowledge[idOf(place)].learnedName.toLowerCase()));
  const publicLabels = new Map();
  const publicLabelFor = place => {
    if (publicLabels.has(idOf(place))) return publicLabels.get(idOf(place));
    const label = labelFor(place);
    const containsPrivateName = place.publicLabel && [...byId.values()].some(candidate => {
      const name = String(candidate.name || candidate.entityName || '').trim();
      return name && label.toLowerCase().includes(name.toLowerCase());
    });
    const result = containsPrivateName ? labelFor({ ...place, publicLabel: '' }) : label;
    publicLabels.set(idOf(place), result);
    return result;
  };
  const replacements = [];
  for (const place of byId.values()) {
    const learned = knowledge[idOf(place)]?.learnedName;
    for (const source of [place.name, place.entityName, place.title, place.entityTitle]) {
      if (typeof source !== 'string' || !source.trim() || source === (learned || publicLabelFor(place))) continue;
      replacements.push({ source: source.trim(), label: learned || publicLabelFor(place), known: Boolean(learned) });
    }
  }
  // Prefer full names over shorter names, and redact shared names when any
  // matching place is unknown. Stable entity IDs disambiguate place objects.
  replacements.sort((a, b) => b.source.length - a.source.length || Number(b.known) - Number(a.known));
  const compileReplacement = ({ source, label }) => ({
    pattern: new RegExp(`(?<![\\p{L}\\p{N}_])${escapePattern(source)}(?![\\p{L}\\p{N}_])`, 'giu'),
    label,
    labelWithoutArticle: label.replace(/^(the|a|an)\s+/i, ''),
  });
  const used = new Set();
  const nameRules = replacements.filter(({ source }) => {
    const key = source.toLowerCase();
    if (used.has(key) || publicGeography.has(key)) return false;
    used.add(key);
    return true;
  }).map(compileReplacement);
  const physicalRules = [];
  for (const place of byId.values()) {
    const learned = knowledge[idOf(place)];
    if (!learned?.learnedName || learned.locationKnown) continue;
    for (const source of new Set([place.name, place.entityName, learned.learnedName].filter(Boolean))) {
      if (publicGeography.has(source.toLowerCase())) continue;
      physicalRules.push(compileReplacement({ source, label: publicLabelFor(place) }));
    }
  }
  const signRules = [...new Set(visibleTexts)].sort((a, b) => b.length - a.length)
    .map(text => new RegExp(escapePattern(text), 'giu'));
  const replaceNames = (value, rules) => {
    let result = value;
    for (const { pattern, label, labelWithoutArticle } of rules) {
      result = result.replace(pattern, (match, offset, text) =>
        /\b(the|a|an)\s+$/i.test(text.slice(0, offset)) ? labelWithoutArticle : label);
    }
    return result;
  };
  const protectVisibleText = value => {
    const preserved = [];
    let result = String(value ?? '');
    for (const pattern of signRules) {
      result = result.replace(pattern, match => {
        const token = `\uE000${preserved.length}\uE001`;
        preserved.push(match);
        return token;
      });
    }
    return { value: result, restore: text => text.replace(/\uE000(\d+)\uE001/g, (match, index) => preserved[index] ?? match) };
  };
  // Each projection belongs to one knowledge snapshot. Bound the text cache so
  // a long conversation cannot retain an unlimited number of obsolete strings.
  const cachedText = physical => {
    const cache = new Map();
    return value => {
      const text = String(value ?? '');
      if (cache.has(text)) return cache.get(text);
      const protectedText = protectVisibleText(text);
      const named = replaceNames(protectedText.value, nameRules);
      const result = protectedText.restore(physical ? replaceNames(named, physicalRules) : named);
      if (text.length <= 4096) {
        if (cache.size >= 256) cache.delete(cache.keys().next().value);
        cache.set(text, result);
      }
      return result;
    };
  };
  const sanitizeText = cachedText(false);
  const sanitizePhysicalText = cachedText(true);
  const placeLabelFor = value => {
    const place = byId.get(idOf(value)) || value;
    const known = knowledge[idOf(place)];
    return known?.learnedName && known.locationKnown ? known.learnedName : publicLabelFor(place);
  };
  const sanitize = (value, key = '') => {
    if (typeof value === 'string') return key === 'symbol' || /(^id|Id$|Ids$|^turnId$|^baseTurnId$|Type$|^shapeType$)/.test(key) ? value
      : /^(placeName|destinationName|containingPlaceName|targetName|completionLabel|label|recentNarration)$/.test(key) ? sanitizePhysicalText(value) : sanitizeText(value);
    if (Array.isArray(value)) return value.map(entry => sanitize(entry, key));
    if (!value || typeof value !== 'object') return value;
    const result = Object.fromEntries(Object.entries(value)
      .filter(([entryKey]) => !['signs', 'privatePlaces', 'privatePlaceNames', 'npcPlaceKnowledgeByResponderId', 'placePresentation'].includes(entryKey))
      .map(([entryKey, entry]) => [entryKey, sanitize(entry, entryKey)]));
    if (value.role === 'assistant' && typeof value.content === 'string') result.content = sanitizePhysicalText(value.content);
    const place = byId.get(idOf(value));
    // A location wrapper has a placeId but is not a place record.
    if (place && (value.idEntity || value.name || value.placeName || value.targetName || value.label)) {
      const known = knowledge[idOf(place)];
      const identified = Boolean(known?.learnedName && known.locationKnown);
      const label = placeLabelFor(place);
      if (value.idEntity) Object.assign(result, {
        name: label, entityName: label, title: '', entityTitle: '',
        publicLabel: publicLabelFor(place), identityKnownToPc: identified, nameKnownToPc: Boolean(known?.learnedName),
        locationKnownToPc: Boolean(known?.locationKnown),
        description: identified ? sanitizeText(value.description || value.entityDesc || '')
          : replaceNames(sanitizeText(place.publicDescription || `You can see ${publicLabelFor(place)}.`),
              [...new Set([place.name, place.entityName].filter(Boolean))].map(source => compileReplacement({ source, label: publicLabelFor(place) }))),
      });
      if ('entityDesc' in result) result.entityDesc = result.description;
      if ('placeName' in value) result.placeName = label;
      if ('targetName' in value) result.targetName = label;
      if ('label' in value) result.label = label;
    }
    return result;
  };
  return { sanitize, sanitizeText, sanitizePhysicalText, labelFor, placeLabelFor };
};
module.exports = { createPlaceIdentity, labelFor, visibleSignText };
